import moment from 'moment';
import React, { useMemo, useContext, useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  Flex,
  ContextMenu as BaseContextMenu,
  FlexCenter,
  FlexMiddle,
  Tooltip,
  TextWithTooltip,
  FlexSpace
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as CalendarViewIconBase } from '@ubeya/shared-web/assets/calendar.svg';
import { ReactComponent as MultiDayIconBase } from '@ubeya/shared-web/assets/multi-day-event.svg';
import { ReactComponent as ArrowsCircleIconBase } from '@ubeya/shared-web/assets/arrows-circle.svg';
import { ReactComponent as BasePlayIcon } from '@ubeya/shared-web/assets/play.svg';
import { ReactComponent as BasePauseIcon } from '@ubeya/shared-web/assets/pause.svg';

const PositionLabel = styled(TextWithTooltip)`
  display: block;
  max-width: 100%;
  min-width: 0;
`;

const PositionLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PositionDetails = styled.div`
  font-size: 12px;
  line-height: 16px;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

const Label = styled(TextWithTooltip)`
  font-size: 12px;
  width: 100%;
  min-width: 0;
`;

const PlayIcon = styled(BasePlayIcon)`
  width: 16px;
  min-width: 16px;
  height: 16px;

  path {
    fill: ${({ theme, $color }) => $color || theme.colors.greenPositive};
  }
`;

const PauseIcon = styled(BasePauseIcon)`
  width: 16px;
  min-width: 16px;
  height: 16px;

  path {
    fill: ${({ theme, $color }) => $color || theme.colors.greenPositive};
  }
`;

const MultiDayIcon = styled(MultiDayIconBase)`
  width: 16px;
  min-width: 16px;
  height: 16px;
  path {
    fill: ${({ theme }) => theme.colors.white};
  }
`;

const SideBarCalendarViewIcon = styled(CalendarViewIconBase)`
  width: 15px;
  min-width: 15px;
  height: 15px;
  path {
    fill: ${({ theme }) => theme.colors.white};
  }
`;

const ArrowsCircleIcon = styled(ArrowsCircleIconBase)`
  width: 13px;
  min-width: 13px;
  height: 13px;

  path {
    stroke: ${({ theme, $color }) => $color || theme.colors.greenPositive};
    fill: none;
  }
`;

const LeftToBookWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 15px;
  height: 15px;
  z-index: 2;
  font-size: 11px;
  padding: 0 4px;
  background-color: ${({ theme, $color }) => $color || theme.colors.greenPositive};
  border: 1px solid ${({ theme }) => theme.colors.white};
  position: absolute;
  left: -8px;
  top: -7px;
  color: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
`;

const Card = styled(Flex)`
  border: 1px solid ${({ $color, theme }) => $color || theme.colors.greenPositive};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ $color, theme }) => $color || theme.colors.greenPositive};
  border-radius: 4px;
  flex: 1;
  z-index: 1;
  width: 100%;
  min-width: 0;
  overflow: unset;
  transition: box-shadow 0.2s ease-in-out;

  ${({ selected }) =>
    selected &&
    css`
      border-width: 2px;
    `}

  &:hover {
    box-shadow: ${({ $color, theme }) => `0 0 4px 3px ${$color || theme.colors.greenPositive}`};
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  padding: 4px;
  flex: 1;
  min-width: 0;
  width: ${({ $isSideIconShown }) => ($isSideIconShown ? 'calc(100% - 20px)' : '100%')};
`;

const SideIconWrapper = styled(FlexCenter)`
  min-height: 100%;
  width: 20px;
  background-color: ${({ theme, $color }) => $color || theme.colors.greenPositive};
`;

const HoursAndIconsWrapper = styled(FlexSpace)`
  align-items: center;
  column-gap: 4px;
  row-gap: 2px;
  flex-wrap: wrap;
`;

const Hours = styled.div`
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
`;

const Wrapper = styled(FlexMiddle)`
  width: 100%;
  cursor: pointer;
  position: relative;
  background: transparent;

  &:hover {
    .circle-icon {
      visibility: visible;
    }
  }

  ${({ $isContextMenuOpen }) =>
    $isContextMenuOpen &&
    css`
      .circle-icon {
        visibility: visible;
      }
    `}
`;

const IconsWrapper = styled(Flex)`
  align-items: center;
  gap: 4px;
`;

const ContextMenu = styled(BaseContextMenu)`
  position: absolute;
  right: -8px;
  top: -7px;
  background: (${({ theme }) => theme.colors.white});
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  .circle-icon {
    padding: 1px 0;
    max-width: 20px;
    max-height: 20px;
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.grey2};
    visibility: hidden;

    &:hover {
      border-color: ${({ theme }) => theme.colors.grey3};
    }
  }
`;

export const OpenShiftsSectionField = ({
  shift,
  onShiftClick,
  onPublishOpenShift,
  selected,
  onOpenShiftClick,
  hideContextOptions = false,
  showLabel
}) => {
  const { timeFormat, t } = useContext(ConfigContext);
  const ref = useRef();
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

  const {
    startTime,
    endTime,
    position,
    label,
    needed,
    stats,
    isPaused,
    publicationId,
    isWeeklyPlanner,
    shiftGroupId,
    isMultiDay,
    name,
    location
  } = shift;

  const onEdit = useCallback(
    (e) => {
      onShiftClick?.(e, ref);
    },
    [onShiftClick]
  );

  const openShiftContextOptions = useMemo(
    () => [
      {
        label: t('edit'),
        onClick: onEdit
      },
      {
        label: t('publish'),
        onClick: onPublishOpenShift
      }
    ],
    [t, onEdit, onPublishOpenShift]
  );

  const labelId = label?.id;
  const currentlyBooked = stats?.booked + stats?.bookedStandBy;
  const leftToBook = needed - currentlyBooked;

  const displayName = useMemo(() => {
    if (labelId && label?.title) {
      return label.title;
    }
    if (name) {
      return name;
    }
    return null;
  }, [labelId, label?.title, name]);

  return (
    <Wrapper ref={ref} $isContextMenuOpen={isContextMenuOpen} onClick={onOpenShiftClick}>
      <Card $color={showLabel && label?.color} selected={selected}>
        {!isWeeklyPlanner && (
          <Tooltip body={t('shiftIsPartOfEvent')}>
            <SideIconWrapper $color={showLabel && label?.color}>
              {isMultiDay ? <MultiDayIcon /> : <SideBarCalendarViewIcon />}
            </SideIconWrapper>
          </Tooltip>
        )}

        <CardContent $isSideIconShown={!isWeeklyPlanner}>
          <PositionDetails>
            <PositionLabelWrapper>
              {isWeeklyPlanner && Boolean(shiftGroupId) && (
                <Tooltip body={t('shiftIsRecurring')}>
                  <ArrowsCircleIcon $color={showLabel && label?.color} />
                </Tooltip>
              )}{' '}
              <PositionLabel>{position ? `${t(position.label)}` : '—'}</PositionLabel>
            </PositionLabelWrapper>
            <Label $color={label?.color}>{displayName ? displayName : location?.name}</Label>
          </PositionDetails>

          <HoursAndIconsWrapper>
            <Hours>
              {moment(startTime).format(timeFormat)} {endTime ? `- ${moment(endTime).format(timeFormat)}` : ''}
            </Hours>

            <IconsWrapper>
              {!!publicationId && !isPaused && (
                <Tooltip body={t('allShiftsWerePublishedAndResumed')}>
                  <PlayIcon $color={showLabel && label?.color} />
                </Tooltip>
              )}
              {isPaused && (
                <Tooltip body={t('allShiftsWerePublishedAndPaused')}>
                  <PauseIcon $color={showLabel && label?.color} />
                </Tooltip>
              )}
            </IconsWrapper>
          </HoursAndIconsWrapper>
        </CardContent>
      </Card>

      {leftToBook > 0 && (
        <Tooltip body={t('leftToBook')}>
          <LeftToBookWrapper $color={showLabel && label?.color}>{leftToBook}</LeftToBookWrapper>
        </Tooltip>
      )}

      {!hideContextOptions && (
        <ContextMenu
          $color={showLabel && label?.color}
          options={openShiftContextOptions}
          usePortal
          hideIconBackground
          onOpenChange={setIsContextMenuOpen}
        />
      )}
    </Wrapper>
  );
};
